import { useTheme } from '@/src/context/ThemeContext';
import { UseCaseLandingConfig, UseCaseLandingScreen } from '@/src/presentation/screens/UseCaseLandingScreen';
import Head from 'expo-router/head';
import { useRouter } from 'expo-router';

const PAGE_URL = 'https://tournaly.app/double-elimination-bracket';
const OG_IMAGE = 'https://tournaly.app/og-image.png';

const CONFIG: UseCaseLandingConfig = {
  badge: 'Double & Triple Elimination',
  badgeEs: 'Eliminación Doble y Triple',
  badgeIcon: 'git-branch-outline',
  title: 'Double Elimination',
  titleEs: 'Brackets de',
  titleAccent: 'Bracket Maker',
  titleAccentEs: 'Eliminación Doble',
  subtitle:
    'Every player gets a second chance. Winners and losers brackets are auto-generated, grand final reset logic included. Triple elimination (3 losses before out) also supported.',
  subtitleEs:
    'Cada jugador tiene una segunda oportunidad. Los brackets de ganadores y perdedores se generan automáticamente, con lógica de final decisiva incluida. Eliminación triple (3 derrotas) también disponible.',
  steps: [
    {
      title: 'Add participants',
      titleEs: 'Añade participantes',
      desc: 'Enter players manually or invite them by email and invite code.',
      descEs: 'Introduce jugadores manualmente o invítalos por email y código de invitación.',
    },
    {
      title: 'Auto-generate both brackets',
      titleEs: 'Genera ambos brackets automáticamente',
      desc: 'Winners and losers brackets are built instantly. Losing once drops you to the losers side, not out.',
      descEs: 'Los brackets de ganadores y perdedores se crean al instante. Perder una vez te pasa al lado de perdedores, no te elimina.',
    },
    {
      title: 'Fight back through losers',
      titleEs: 'Remonta por el bracket de perdedores',
      desc: 'Losers bracket survivors meet the winners bracket champion in the grand final — with reset logic if needed.',
      descEs: 'Los supervivientes del bracket de perdedores se enfrentan al campeón del de ganadores en la gran final — con reset si es necesario.',
    },
  ],
  features: [
    {
      icon: 'git-branch-outline',
      color: '#FF6B35',
      title: 'Winners & Losers Brackets',
      titleEs: 'Brackets de Ganadores y Perdedores',
      desc: 'Both brackets auto-generated and linked — no manual setup required.',
      descEs: 'Ambos brackets se generan automáticamente y están enlazados — sin configuración manual.',
    },
    {
      icon: 'refresh-outline',
      color: '#4ECDC4',
      title: 'Grand Final Reset Logic',
      titleEs: 'Lógica de Reset en Gran Final',
      desc: 'If the losers bracket champion wins the grand final, a reset match is automatically triggered.',
      descEs: 'Si el campeón del bracket de perdedores gana la gran final, se dispara automáticamente un partido de reset.',
    },
    {
      icon: 'shield-outline',
      color: '#7C3AED',
      title: 'Triple Elimination',
      titleEs: 'Eliminación Triple',
      desc: 'Need 3 losses before elimination? Triple elimination format is also supported for 4-player brackets.',
      descEs: '¿Necesitas 3 derrotas antes de la eliminación? El formato de eliminación triple también es compatible para brackets de 4 jugadores.',
    },
    {
      icon: 'share-social-outline',
      color: '#06B6D4',
      title: 'Export PNG & PDF',
      titleEs: 'Exportar PNG y PDF',
      desc: 'Download both brackets as a clean image or PDF — perfect for sharing or printing.',
      descEs: 'Descarga ambos brackets como imagen o PDF — perfecto para compartir o imprimir.',
    },
    {
      icon: 'bar-chart-outline',
      color: '#45B7D1',
      title: 'Real-Time Updates',
      titleEs: 'Actualizaciones en Tiempo Real',
      desc: 'Results propagate instantly across both brackets as each match is played.',
      descEs: 'Los resultados se propagan al instante por ambos brackets a medida que se juegan los partidos.',
    },
    {
      icon: 'phone-portrait-outline',
      color: '#A78BFA',
      title: 'Mobile Apps',
      titleEs: 'Apps Móviles',
      desc: 'Manage your double elimination tournament from iOS or Android — free download.',
      descEs: 'Gestiona tu torneo de eliminación doble desde iOS o Android — descarga gratuita.',
    },
  ],
  relatedBlogSlug: '2026-03-06-double-elimination-bracket-guide',
  relatedBlogTitle: 'Double Elimination Brackets Explained — Complete Guide',
  relatedBlogTitleEs: 'Brackets de Eliminación Doble Explicados — Guía Completa',
  relatedBlogSummary: 'Everything you need to know about double elimination: how it works, when to use it and how to run it.',
  relatedBlogSummaryEs: 'Todo lo que necesitas saber sobre eliminación doble: cómo funciona, cuándo usarla y cómo organizarla.',
};

const title = 'Double Elimination Bracket Maker — Free Online Tool | Tournaly';
const description =
  'Create double elimination tournament brackets online for free. Auto winners/losers brackets, grand final reset, triple elimination support. Up to 16 players free.';

export default function DoubleEliminationBracketPage() {
  const { colors, isDark, toggleTheme } = useTheme();
  const router = useRouter();

  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Tournaly — Double Elimination Bracket Maker',
    applicationCategory: 'SportsApplication',
    operatingSystem: 'Web, iOS, Android',
    url: PAGE_URL,
    description,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@type': 'Organization', name: 'Tournaly', url: 'https://tournaly.app' },
  });

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Tournaly" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="keywords" content="double elimination bracket, double elimination tournament, bracket maker, losers bracket, grand final reset, triple elimination" />
        <script type="application/ld+json">{jsonLd}</script>
      </Head>
      <UseCaseLandingScreen
        config={CONFIG}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        onGetStarted={() => router.push('/auth/register' as any)}
        onSignIn={() => router.push('/auth/login' as any)}
        onBlogPostPress={(slug) => {
          if (slug === 'blog') router.push('/blog' as any);
          else if (slug === 'privacy') router.push('/privacy' as any);
          else if (slug === 'terms') router.push('/terms' as any);
          else router.push(`/blog/${slug}` as any);
        }}
        colors={colors}
      />
    </>
  );
}
